/**
 * Ordering the Trends cards, and summarising a brief in one line.
 *
 * Pure and dependency-free so it can be tested — CRA's jest cannot resolve
 * react-router-dom v7, so anything worth a test has to import neither it nor
 * react-dnd. Same reason `subjectTree.ts` and `artifactGroups.ts` are separate.
 */
import type { TrendBrief, TrendBriefKeyword, TrendStory } from '../../types';

/** Live terms first, then the back catalogue — each band ordered by usefulness.
 *
 * THREE KEYS, in this order:
 *
 *  1. `is_current`. A term still in Google's feed outranks one that dropped out
 *     however good the latter looked, because the whole premise of the page is
 *     that a spike is a window and an open one beats a closed one.
 *  2. proposals. Within a band, feed rank orders by how hard the country is
 *     searching, which is not the question "what should I write". A term at
 *     rank 19 that came back with three proposed pages is worth more of the
 *     screen than the celebrity name at rank 2 that came back with none.
 *  3. rank for live rows; last-seen for dropped ones, since their rank is a
 *     frozen reading from whenever they were last polled and says nothing about
 *     now, whereas "how recently did this matter" does.
 */
export function sortStories(stories: TrendStory[]): TrendStory[] {
  return [...stories].sort((a, b) => {
    if (a.is_current !== b.is_current) return a.is_current ? -1 : 1;

    const ap = a.brief?.proposals.length ?? 0;
    const bp = b.brief?.proposals.length ?? 0;
    if (ap !== bp) return bp - ap;

    if (a.is_current) return a.rank - b.rank;
    return new Date(b.last_seen_at).getTime() - new Date(a.last_seen_at).getTime();
  });
}

/** How long ago a dropped term was last in the feed. */
export function lastSeenLabel(story: TrendStory, now: Date = new Date()): string {
  const ms = now.getTime() - new Date(story.last_seen_at).getTime();
  const hours = Math.floor(ms / 3_600_000);
  if (hours < 1) return 'just dropped out';
  if (hours < 24) return `last seen ${hours}h ago`;
  const days = Math.round(hours / 24);
  return `last seen ${days}d ago`;
}

/** The best keyword to show on a card that has no proposals.
 *
 * Highest volume wins, but a term with no volume at all never does — a null
 * reads as 0 in a naive sort and would otherwise beat a real number.
 */
export function headlineKeyword(brief: TrendBrief | null): TrendBriefKeyword | null {
  if (!brief?.keywords.length) return null;
  const withVolume = brief.keywords.filter(k => (k.monthly_searches ?? 0) > 0);
  const pool = withVolume.length ? withVolume : brief.keywords;
  return pool.reduce((best, k) =>
    (k.monthly_searches ?? 0) > (best.monthly_searches ?? 0) ? k : best,
  );
}

export type BriefState =
  | 'unresearched'
  | 'working'
  | 'ready'
  | 'ready-empty'
  | 'partial'
  | 'failed';

/**
 * What the card should SAY about itself.
 *
 * `ready-empty` and `partial` are separate states on purpose. A term researched
 * successfully that yielded no pages is a real and useful answer — most trending
 * terms are a name or a score. A term where Google Ads could not be reached is
 * not an answer at all, and showing both as "no proposals" would tell the reader
 * to skip a story that might be the best one on the page.
 *
 * ONLY THE ADS HALF IS DISQUALIFYING, and getting that wrong is what made a card
 * with no Create button unreadable. This used to treat EITHER skip reason as
 * `partial`, which short-circuited before the proposals check — so a term that
 * was researched fine and simply wasn't worth writing about showed "No stories
 * in the last 72 hours" and never got to say the thing that mattered.
 *
 * The asymmetry is real, not a presentation choice: `_propose` returns zero
 * without keywords, so no Ads data means no judgement was ever made. Missing
 * NEWS does not stop a proposal — the feed carries its own headlines — so it is
 * a footnote on the card, not its state.
 */
export function briefState(brief: TrendBrief | null, enriching = false): BriefState {
  if (enriching) return 'working';
  if (!brief || brief.status === 'PENDING') return 'unresearched';
  if (brief.status === 'ENRICHING') return 'working';
  if (brief.status === 'FAILED') return 'failed';
  // Something to offer beats any caveat about how it was gathered.
  if (brief.proposals.length) return 'ready';
  // No keywords means nothing could be proposed — the model was never asked.
  if (brief.ads_skipped_reason) return 'partial';
  // Everything that matters ran, and the answer was "no".
  return 'ready-empty';
}

/**
 * The sentence under a card that has nothing to offer, or offers less than it should.
 *
 * Answers "why is there no Create button" — which was the question a reader had
 * to guess at, because the news caveat was crowding out the verdict.
 */
export function briefNotice(brief: TrendBrief | null, state: BriefState): string | null {
  switch (state) {
    case 'failed':
      return brief?.last_error || 'Researching this term failed.';
    case 'partial':
      // The disqualifying half. Named on its own so it is not read as one of
      // two equally-weighted complaints.
      return (
        brief?.ads_skipped_reason ||
        'No demand data, so no pages could be suggested.'
      );
    case 'ready-empty':
      return 'Researched - nothing here looks durable enough to be worth a page.';
    default:
      return null;
  }
}

/**
 * The smaller caveat, shown under the verdict rather than instead of it.
 *
 * Missing news does not stop a proposal (the feed carries its own headlines), so
 * it is a footnote — but it is still worth saying, because a reader deciding
 * whether to re-research wants to know the story half came back empty.
 */
export function briefFootnote(
  brief: TrendBrief | null,
  state: BriefState,
): string | null {
  if (!brief) return null;
  if (state === 'unresearched' || state === 'working' || state === 'failed') return null;
  if (!brief.news_skipped_reason) return null;
  // On a `partial` card the Ads reason is already the headline; repeating the
  // news one below it is the useful detail, not a duplicate.
  return brief.news_skipped_reason;
}

/** "12,000/mo · low competition · $4.20 top bid" */
export function describeKeyword(k: TrendBriefKeyword): string {
  const bits: string[] = [];
  if (k.monthly_searches != null) bits.push(`${k.monthly_searches.toLocaleString()}/mo`);
  if (k.competition) bits.push(`${k.competition.toLowerCase()} competition`);
  if (k.top_bid != null && k.top_bid > 0) bits.push(`$${k.top_bid.toFixed(2)} top bid`);
  return bits.join(' · ');
}

/**
 * A skip reason shared by enough cards that it is about the ACCOUNT, not the term.
 *
 * "Google Ads isn't connected for this account" is true of every card at once.
 * Printed per-card it becomes twenty-five lines of identical text that bury the
 * headlines and read as twenty-five separate problems; the reader has to notice
 * the repetition themselves to work out it is one setting.
 *
 * Returns the message to hoist to the section, or null when the reasons differ
 * — a term with genuinely no demand is a fact about that term and belongs on
 * its own card.
 */
export function sharedSkipReason(stories: TrendStory[]): string | null {
  const researched = stories.filter(s => s.brief && s.brief.status === 'READY');
  // Two is a coincidence; three of three is a setting.
  if (researched.length < 3) return null;

  // Compared against the first rather than collected into a Set: the tsconfig
  // target predates Set iteration, and "do they all match this one" is the
  // question anyway.
  const first = researched[0].brief?.ads_skipped_reason ?? '';
  if (!first) return null;
  const allAgree = researched.every(
    s => (s.brief?.ads_skipped_reason ?? '') === first,
  );
  return allAgree ? first : null;
}

/**
 * Keep the list where the reader left it.
 *
 * `sortStories` ranks by whether a term has proposals — which is right on
 * arrival and wrong every time after, because a brief FINISHING is exactly when
 * a card gains proposals. The card you were watching, and had been reading for
 * ten seconds, leaps to the top of the list at the moment its result lands. From
 * the reader's side the card does not arrive, it vanishes: they are still
 * looking at the row it used to occupy, which now holds something else, and the
 * term they were researching is gone before they can name it.
 *
 * So the order is decided ONCE per deliberate action — first load, refresh,
 * geo or window change — and every background refetch after that reuses it.
 * Terms that were not in the previous order are new to the feed and are sorted
 * normally, appended at the end where they cannot displace anything.
 */
export function stableOrder(next: TrendStory[], previousIds: number[]): TrendStory[] {
  if (previousIds.length === 0) return sortStories(next);

  const rank = new Map(previousIds.map((id, i) => [id, i]));
  const known: TrendStory[] = [];
  const fresh: TrendStory[] = [];
  for (const story of next) {
    (rank.has(story.trending_search_id) ? known : fresh).push(story);
  }
  known.sort(
    (a, b) =>
      (rank.get(a.trending_search_id) ?? 0) - (rank.get(b.trending_search_id) ?? 0),
  );
  return [...known, ...sortStories(fresh)];
}
